import {
  cloneElement,
  Fragment,
  isValidElement,
  useEffect,
  useId,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { resolveEvidence } from '../lib/evidence';

/* ------------------------------------------------------------------ icons */

const paths: Record<string, ReactNode> = {
  command: (
    <>
      <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z" />
      <path d="M17 14v6M14 17h6" />
    </>
  ),
  bolt: <path d="m13 2-9 12h7l-1 8 9-12h-7z" />,
  network: (
    <>
      <circle cx="12" cy="5" r="2.5" />
      <circle cx="5" cy="18" r="2.5" />
      <circle cx="19" cy="18" r="2.5" />
      <path d="m10.7 7.1-4.4 8.7M13.3 7.1l4.4 8.7M7.5 18h9" />
    </>
  ),
  pipeline: <path d="M4 5h16M6 12h12M9 19h6" />,
  anchor: (
    <>
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v13M5 11H2M22 11h-3M5 16c1 3 3.3 5 7 5s6-2 7-5M5 14v2M19 14v2" />
    </>
  ),
  shield: <path d="M12 3 5 6v5c0 4.6 2.8 8 7 10 4.2-2 7-5.4 7-10V6z" />,
  capital: <path d="M3 8h18M5 8v10M9 8v10M15 8v10M19 8v10M3 19h18M12 3l9 5H3z" />,
  government: <path d="M4 21h16M6 18h12M7 9v9M12 9v9M17 9v9M3 8h18L12 3z" />,
  infrastructure: (
    <>
      <path d="M12 2v7M12 15v7M4.9 4.9l5 5M14.1 14.1l5 5M2 12h7M15 12h7M4.9 19.1l5-5M14.1 9.9l5-5" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  graph: (
    <>
      <circle cx="5" cy="17" r="2" />
      <circle cx="12" cy="7" r="2" />
      <circle cx="19" cy="15" r="2" />
      <path d="m6.2 15.4 4.6-6.8M13.7 8.4l3.6 5.2M7 17h10" />
    </>
  ),
  evidence: (
    <>
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M14 3v5h5M9 13l2 2 4-5" />
    </>
  ),
  meeting: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 10h18M8 15h.01M12 15h.01M16 15h.01" />
    </>
  ),
  decision: <path d="M12 3v18M12 7h6l2 2-2 2h-6M12 14H6l-2 2 2 2h6" />,
  review: (
    <>
      <path d="M20 11a8 8 0 1 1-2.3-5.7L20 8" />
      <path d="M20 3v5h-5M8 12l3 3 5-6" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21h-4v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H3v-4h.1a1.7 1.7 0 0 0 1.5-1A1.7 1.7 0 0 0 4.3 7l-.1-.1L7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.5V3h4v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.5 1h.1v4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-4-4" />
    </>
  ),
  bell: <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />,
  arrow: <path d="M5 12h14M14 7l5 5-5 5" />,
  spark: (
    <path d="m12 2 1.4 5.6L19 9l-5.6 1.4L12 16l-1.4-5.6L5 9l5.6-1.4zM19 17l.6 2.4L22 20l-2.4.6L19 23l-.6-2.4L16 20l2.4-.6z" />
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <circle cx="19" cy="12" r="1" fill="currentColor" />
    </>
  ),
  monitor: (
    <>
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M20.8 15.2A8.5 8.5 0 0 1 8.8 3.2 8.5 8.5 0 1 0 20.8 15.2Z" />,
};

export type IconName = keyof typeof paths;

export function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

export const THEMES: { value: 'system' | 'light' | 'dark'; label: string; title: string }[] = [
  { value: 'system', label: 'Auto', title: 'Follow the system appearance' },
  { value: 'light', label: 'Light', title: 'Always light' },
  { value: 'dark', label: 'Dark', title: 'Always dark' },
];

export function ThemeSwitch({
  value,
  onChange,
}: {
  value: 'system' | 'light' | 'dark';
  onChange: (v: 'system' | 'light' | 'dark') => void;
}) {
  return (
    <div className="seg theme-switch" role="group" aria-label="Theme">
      {THEMES.map((t) => (
        <button
          key={t.value}
          type="button"
          title={t.title}
          aria-pressed={value === t.value}
          className={value === t.value ? 'on' : ''}
          onClick={() => onChange(t.value)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export const navigation: { label: string; icon: IconName }[] = [
  { label: 'Command', icon: 'command' },
  { label: 'Tactical 45', icon: 'bolt' },
  { label: 'Strategic Network', icon: 'network' },
  { label: 'Partner Pipeline', icon: 'pipeline' },
  { label: 'Ecosystem Anchors', icon: 'anchor' },
  { label: 'Regulated Stack', icon: 'shield' },
  { label: 'Capital & Distribution', icon: 'capital' },
  { label: 'Government', icon: 'government' },
  { label: 'Infrastructure', icon: 'infrastructure' },
  { label: 'Partner Graph', icon: 'graph' },
  { label: 'Evidence', icon: 'evidence' },
  { label: 'Meetings', icon: 'meeting' },
  { label: 'Decisions', icon: 'decision' },
  { label: 'Weekly Review', icon: 'review' },
];

/* ----------------------------------------------------------- Bridge45 UI */

export function Card({
  title,
  sub,
  right,
  children,
  className = '',
  flush = false,
  foot,
}: {
  title?: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
  className?: string;
  flush?: boolean;
  foot?: ReactNode;
}) {
  return (
    <section className={`card ${flush ? 'flush' : ''} ${className}`.trim()}>
      {(title || right) && (
        <header className="card-head">
          {title && <h3>{title}</h3>}
          {sub && <span className="sub">{sub}</span>}
          <div className="spacer" />
          {right}
        </header>
      )}
      <div className="card-body">{children}</div>
      {foot}
    </section>
  );
}

export function Stat({
  label,
  value,
  foot,
  size = '',
  tone,
}: {
  label: ReactNode;
  value: ReactNode;
  foot?: ReactNode;
  size?: '' | 'sm' | 'lg';
  tone?: string;
}) {
  return (
    <div className="stat">
      <span className="label">{label}</span>
      <span className={`value ${size}`} style={tone ? { color: `var(--${tone})` } : undefined}>
        {value}
      </span>
      {foot && <span className="foot">{foot}</span>}
    </div>
  );
}

export function Chip({
  tone = '',
  children,
  lg = false,
}: {
  tone?: string;
  children?: ReactNode;
  lg?: boolean;
}) {
  return <span className={`chip ${tone} ${lg ? 'lg' : ''}`.trim()}>{children}</span>;
}

export function StagePill({ value }: { value: string }) {
  const def = resolveEvidence(value);
  return (
    <span className={`ev ev-${def.evClass}`} title={def.line}>
      {def.id}
    </span>
  );
}

export function EvidenceBadge({ level, showName = false }: { level: string; showName?: boolean }) {
  const def = resolveEvidence(level);
  return (
    <span className={`ev ev-${def.evClass}`} title={def.line}>
      {def.id}
      {showName ? ` · ${def.name}` : ''}
    </span>
  );
}

export function GateLight({ state = 'red', label }: { state?: string; label?: ReactNode }) {
  return (
    <span className="gate">
      <span className={`dot ${state}`} />
      <span>{label}</span>
    </span>
  );
}

export function Field({
  label,
  help,
  children,
  style,
}: {
  label?: ReactNode;
  help?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
}) {
  const id = useId();
  return (
    <div className="field" style={style}>
      {label && <label htmlFor={id}>{label}</label>}
      {isValidElement(children)
        ? cloneElement(children as ReactElement<{ id?: string }>, { id })
        : children}
      {help && <span className="help">{help}</span>}
    </div>
  );
}

export function Text({
  label,
  value,
  onChange,
  placeholder,
  help,
  type = 'text',
}: {
  label?: ReactNode;
  value?: string | number;
  onChange: (v: string) => void;
  placeholder?: string;
  help?: ReactNode;
  type?: string;
}) {
  return (
    <Field label={label} help={help}>
      <input
        type={type}
        className={type === 'number' ? 'num-input' : undefined}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function Money({
  label,
  value,
  onChange,
  currency,
  help,
}: {
  label: string;
  value?: number;
  onChange: (v: number) => void;
  currency?: string;
  help?: ReactNode;
}) {
  return (
    <Field label={currency ? `${label} (${currency})` : label} help={help}>
      <input
        type="number"
        className="num-input"
        value={value ?? 0}
        step="any"
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
      />
    </Field>
  );
}

export function Area({
  label,
  value,
  onChange,
  placeholder,
  help,
  rows,
}: {
  label?: ReactNode;
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
  help?: ReactNode;
  rows?: number;
}) {
  return (
    <Field label={label} help={help}>
      <textarea
        rows={rows}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

type SelectOption = string | { value: string; label: string };

export function Select({
  label,
  value,
  onChange,
  options,
  help,
}: {
  label?: ReactNode;
  value?: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  help?: ReactNode;
}) {
  return (
    <Field label={label} help={help}>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => {
          const val = typeof o === 'string' ? o : o.value;
          const lbl = typeof o === 'string' ? o : o.label;
          return (
            <option key={val} value={val}>
              {lbl}
            </option>
          );
        })}
      </select>
    </Field>
  );
}

export function Check({
  label,
  checked,
  onChange,
  help,
}: {
  label: ReactNode;
  checked?: boolean;
  onChange: (v: boolean) => void;
  help?: ReactNode;
}) {
  return (
    <label className="check">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {help && (
          <span className="help" style={{ display: 'block' }}>
            {help}
          </span>
        )}
      </span>
    </label>
  );
}

export function Segmented({
  value,
  onChange,
  options,
  toneMap,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  toneMap?: Record<string, string>;
}) {
  return (
    <div className="seg">
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const lbl = typeof o === 'string' ? o : o.label;
        const tone = toneMap ? toneMap[val] || '' : '';
        return (
          <button
            key={val}
            type="button"
            className={value === val ? `on ${tone}`.trim() : ''}
            onClick={() => onChange(val)}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  );
}

export function Modal({
  title,
  sub,
  children,
  onClose,
  footer,
  wide = false,
}: {
  title: ReactNode;
  sub?: ReactNode;
  children?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true">
        <header className="modal-head">
          <h3>{title}</h3>
          {sub && <span className="sub dim tiny">{sub}</span>}
          <div className="spacer" />
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label="Close">
            Close
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}

export function Empty({ children, title }: { children?: ReactNode; title?: ReactNode }) {
  return (
    <div className="empty">
      {title && <strong>{title}</strong>}
      {children}
    </div>
  );
}

export function Banner({
  tone = '',
  children,
}: {
  tone?: string;
  children?: ReactNode;
}) {
  const mapped = tone === 'bad' || tone === 'error' ? 'stop' : tone;
  return <div className={`banner ${mapped}`.trim()}>{children}</div>;
}

export function Pipeline({
  stages,
  value,
  onChange,
  canAdvance = true,
  blockedReason,
}: {
  stages: string[];
  value: string;
  onChange?: (s: string) => void;
  canAdvance?: boolean;
  blockedReason?: string;
}) {
  const idx = stages.indexOf(value);
  return (
    <div className="pipeline">
      {stages.map((s, i) => {
        const cls = i === idx ? 'now' : i < idx ? 'done' : '';
        const forward = i > idx;
        const blocked = forward && !canAdvance;
        return (
          <button
            key={s}
            type="button"
            className={`step ${cls}`}
            disabled={blocked}
            title={blocked ? blockedReason : `Move to ${s}`}
            onClick={() => onChange?.(s)}
          >
            {i + 1}. {s}
          </button>
        );
      })}
    </div>
  );
}

export function BarRow({
  label,
  actual,
  target,
  suffix = '%',
}: {
  label: ReactNode;
  actual: number;
  target?: number;
  suffix?: string;
}) {
  const width = Math.min(100, Math.max(0, actual));
  const over = target !== undefined && actual > target + 5;
  const under = target !== undefined && actual < target - 5;
  return (
    <div className="bar-row">
      <span className="lbl">{label}</span>
      <span className="track">
        <span className={`fill ${over ? 'over' : under ? 'under' : ''}`} style={{ width: `${width}%` }} />
        {target !== undefined && <span className="target" style={{ left: `${Math.min(100, target)}%` }} />}
      </span>
      <span className="val">
        {actual}
        {suffix}
        {target !== undefined && (
          <span className="dim">
            {' '}
            / {target}
            {suffix}
          </span>
        )}
      </span>
    </div>
  );
}

export function KV({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="kv">
      {items.map(([k, v], i) => (
        <Fragment key={i}>
          <dt>{k}</dt>
          <dd>{v === '' || v === null || v === undefined ? <span className="dim">—</span> : v}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function ChecklistRow({
  ok,
  label,
  detail,
}: {
  ok?: boolean;
  label: ReactNode;
  detail?: ReactNode;
}) {
  return (
    <div className="row tight" style={{ padding: '5px 0', alignItems: 'flex-start' }}>
      <span className={`dot ${ok ? 'green' : 'red'}`} style={{ marginTop: 5 }} />
      <span style={{ flex: 1 }}>
        <span style={{ fontSize: 13.5 }}>{label}</span>
        {detail && (
          <span className="dim tiny" style={{ display: 'block' }}>
            {detail}
          </span>
        )}
      </span>
    </div>
  );
}

export function SectionHead({
  title,
  hint,
  right,
}: {
  title: ReactNode;
  hint?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="section-head">
      <h3>{title}</h3>
      {hint && <span className="hint">{hint}</span>}
      <div className="spacer" />
      {right}
    </div>
  );
}

export function PageHeader({
  kicker,
  title,
  description,
  action,
}: {
  kicker: string;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-head row" style={{ alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <div className="dim tiny" style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          {kicker}
        </div>
        <h2>{title}</h2>
        <div className="sub">{description}</div>
      </div>
      {action}
    </div>
  );
}
